import { useState } from "react";
import { Btn } from "@/components/Btn";
import { Badge, StatusBadge } from "@/components/Badge";
import { BidCell } from "@/components/BidCell";
import { Dropdown } from "@/components/Dropdown";
import { I } from "@/components/Icon";
import { Kpi } from "@/components/Kpi";
import { RoasCell, CpaCell } from "@/components/MetricCells";
import { SegRange } from "@/components/SegRange";
import { Spark } from "@/components/Spark";
import { Toolbar } from "@/components/Toolbar";

/**
 * Phase 3A smoke test. Exercises every primitive so we catch token /
 * styling regressions at a glance. Not linked from navigation — the
 * root route serves this page until Phase 3B introduces routing.
 */
export function PrimitivesDemo() {
  const [period, setPeriod] = useState<"7d" | "30d" | "90d">("7d");
  const [segment, setSegment] = useState<"all" | "active" | "paused">("all");
  const [bid, setBid] = useState<number>(2.35);

  const sparkData = [12, 16, 14, 18, 22, 19, 24, 28, 25, 30];

  return (
    <div style={{ padding: 40, maxWidth: 1280, margin: "0 auto" }}>
      <h1
        className="page-title"
        style={{ margin: 0, marginBottom: 8, fontSize: 42 }}
      >
        Design primitives <em>demo</em>
      </h1>
      <p className="page-sub" style={{ marginBottom: 32 }}>
        Phase 3A smoke test — every atom from the design bundle rendered
        with real tokens. If a token moves, this page breaks first.
      </p>

      <Section title="Buttons">
        <Row>
          <Btn>Default</Btn>
          <Btn variant="primary">Primary</Btn>
          <Btn variant="ghost">Ghost</Btn>
          <Btn variant="danger">Danger</Btn>
          <Btn size="sm">Small</Btn>
          <Btn variant="icon" aria-label="refresh">
            {I.refresh}
          </Btn>
          <Btn variant="primary" icon={I.plus}>
            New campaign
          </Btn>
        </Row>
      </Section>

      <Section title="Badges & status">
        <Row>
          <Badge>Default</Badge>
          <Badge kind="accent">Accent</Badge>
          <Badge kind="warn">Warn</Badge>
          <Badge kind="neg">Negative</Badge>
          <StatusBadge status="ENABLED" />
          <StatusBadge status="PAUSED" />
        </Row>
      </Section>

      <Section title="Dropdown + SegRange">
        <Row>
          <Dropdown<"7d" | "30d" | "90d">
            label="Period"
            value={period}
            onChange={setPeriod}
            options={[
              { value: "7d", label: "Last 7 days" },
              { value: "30d", label: "Last 30 days" },
              { value: "90d", label: "Last 90 days" },
            ]}
          />
          <SegRange<"all" | "active" | "paused">
            value={segment}
            onChange={setSegment}
            options={[
              { value: "all", label: "All" },
              { value: "active", label: "Active" },
              { value: "paused", label: "Paused" },
            ]}
          />
        </Row>
      </Section>

      <Section title="Sparkline">
        <Row>
          <Spark data={sparkData} />
          <Spark data={sparkData} color="var(--c-pos-500)" />
          <Spark data={sparkData} color="var(--c-neg-500)" width={160} height={48} />
        </Row>
      </Section>

      <Section title="KPI cards">
        <div className="kpi-grid">
          <Kpi
            label="Spend"
            value="12,345"
            unit="$"
            delta="8.2%"
            deltaDir="pos"
            spark={sparkData}
          />
          <Kpi
            label="Revenue"
            value="24,910"
            unit="$"
            delta="12.4%"
            deltaDir="pos"
            spark={sparkData.slice().reverse()}
          />
          <Kpi
            label="ROAS"
            value="2.02"
            unit="×"
            delta="3.1%"
            deltaDir="neg"
            spark={sparkData.map((v) => v * 0.9)}
          />
          <Kpi label="Installs" value="1,842" delta="4.0%" deltaDir="pos" spark={sparkData} />
          <Kpi label="Trials" value="612" delta="1.8%" deltaDir="neg" spark={sparkData} />
        </div>
      </Section>

      <Section title="Table cells">
        <div className="card" style={{ padding: 12 }}>
          <Toolbar>
            <span style={{ fontSize: 13, color: "var(--c-text-3)" }}>
              RoasCell / CpaCell / BidCell inside a standard table.
            </span>
          </Toolbar>
          <div className="table-scroll">
            <table className="dt" style={{ minWidth: 600 }}>
              <thead>
                <tr>
                  <th>Keyword</th>
                  <th className="num">Bid</th>
                  <th className="num">CPA</th>
                  <th className="num">ROAS</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>math solver exact</td>
                  <BidCell
                    value={bid}
                    editable
                    onCommit={(next) => setBid(next)}
                  />
                  <CpaCell v={1.22} />
                  <RoasCell v={2.14} />
                </tr>
                <tr>
                  <td>math worksheets</td>
                  <BidCell value={0.85} editable={false} />
                  <CpaCell v={4.2} />
                  <RoasCell v={0.65} />
                </tr>
                <tr>
                  <td>homework help</td>
                  <BidCell value={1.5} editable />
                  <CpaCell v={2.1} />
                  <RoasCell v={1.1} />
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 32 }}>
      <h2
        style={{
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--c-text-3)",
          marginBottom: 12,
        }}
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      {children}
    </div>
  );
}
